import * as React from 'react';
import { Collapse, Stack, Box, Popover, Typography } from '@mui/material';
import { DateChip } from './DateChip';
import { ResponseDueIcon, SchedulingIcon } from '../../components/icons';

export type ItemDate = {
  sequence: number;
  type: 'task' | 'schedule' | 'resp_due' | 'event';
  append?:
    | 'Item Start/Due Date(s)'
    | 'Scheduling Due'
    | 'Message Response Due'
    | 'Event Date(s)';
  onDateChange?: ({ start, end }: { start?: Date; end?: Date }) => {};
  start: Date | null;
  end: Date | null;
  disabled?: boolean;
  icon?: any;
};

const getDefaultAppendByType = (type?: string): string => {
  switch (type) {
    case 'task':
      return 'Item Start/Due Date(s)';
    case 'schedule':
      return 'Scheduling Due';
    case 'resp_due':
      return 'Message Response Due';
    case 'event':
      return 'Event Date(s)';

    default:
      return '';
  }
};

export const getDefaultIconByType = (type?: string): any => {
  switch (type) {
    case 'task':
      return undefined;
    case 'schedule':
      return <SchedulingIcon />;
    case 'resp_due':
      return <ResponseDueIcon />;
    case 'event':
      return undefined;

    default:
      return undefined;
  }
};

export const getDefaultTooltipByType = (
  type?: string,
  start?: boolean,
  end?: boolean
): string => {
  switch (type) {
    case 'task':
      if (!start && !end) {
        return 'Add Item Start/Due Date(s)';
      } else {
        return 'Item Start/Due Date(s)';
      }
    case 'stage':
      return 'Stage Due Date';
    case 'schedule':
      if (!start && !end) {
        return 'Add Scheduling Start/Due Date(s)';
      } else {
        return 'Scheduling Start/Due Date(s)';
      }
    case 'resp_due':
      return 'Message Response Due Date';
    case 'event':
      return 'Event Date(s)';

    default:
      return '';
  }
};

export type ItemDateCollapseProps = {
  dates: ItemDate[];
  open: boolean;
  close: Function;
  anchorEl?: any;
  updateDates?: ({
    start,
    end,
    index,
  }: {
    start?: Date;
    end?: Date;
    index: number;
  }) => {};
  complete?: boolean;
};

export const ItemDateCollapse = ({
  open,
  close,
  dates,
  anchorEl,
  updateDates,
  complete,
}: ItemDateCollapseProps) => {
  const topDate = dates[0];
  const remainingDates = dates.slice(1, dates.length);
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={(event: any) => {
        event.preventDefault();
        event.stopPropagation();
        close();
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      elevation={3}
      PaperProps={{
        sx: {
          bgcolor: 'white',
          px: 2,
        },
      }}
    >
      <Typography variant="overline" sx={{ textDecoration: 'underline' }}>
        All Item Dates
      </Typography>
      <Collapse in={Boolean(anchorEl)} timeout="auto" unmountOnExit>
        <Stack
          spacing={1}
          sx={{
            width: '100%',
            maxWidth: 360,
          }}
        >
          <Box
            onClick={event => {
              event.stopPropagation();
              event.preventDefault();
            }}
          >
            {topDate && (
              <Box
                sx={{
                  mb: theme => theme.spacing(1),
                  // backdropFilter: 'blur(5px)',
                }}
              >
                <DateChip
                  // visibility={'hidden'}
                  complete={complete}
                  value={{
                    start: topDate.start ? new Date(topDate.start) : undefined,
                    end: topDate.end ? new Date(topDate.end) : undefined,
                  }}
                  onDateChange={async ({
                    start,
                    end,
                  }: {
                    start?: Date;
                    end?: Date;
                  }) => {
                    if (updateDates) {
                      await updateDates({ start, end, index: 0 });
                    }
                  }}
                  backgroundColor={'white'}
                  disabled={topDate?.disabled || complete}
                  elevation={1}
                  append={
                    topDate?.append || getDefaultAppendByType(topDate?.type)
                  }
                  icon={getDefaultIconByType(topDate?.type)}
                />
              </Box>
            )}
            {remainingDates?.map((date, index) => (
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  // backdropFilter: 'blur(5px)',
                  my: theme => theme.spacing(2),
                }}
              >
                <DateChip
                  complete={complete}
                  value={{
                    start: date.start ? new Date(date.start) : undefined,
                    end: date.end ? new Date(date.end) : undefined,
                  }}
                  onDateChange={async ({
                    start,
                    end,
                  }: {
                    start?: Date;
                    end?: Date;
                  }) => {
                    if (updateDates) {
                      await updateDates({ start, end, index: index + 1 });
                    }
                  }}
                  backgroundColor={'white'}
                  disabled={date?.disabled || complete}
                  elevation={1}
                  append={date?.append || getDefaultAppendByType(date?.type)}
                  icon={getDefaultIconByType(date?.type)}
                />
              </Box>
            ))}
          </Box>
        </Stack>
      </Collapse>
    </Popover>
  );
};
